import React from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { EditSessionStep, useEditSessionSteps } from '../steps';
import { Session } from '@app/api';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    marginBottom: theme.marginBase * 5,
  },
  topLine: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.marginBase * 2,
  },
  title: {
    ...theme.fonts.h5,
    textTransform: 'uppercase',
    flex: 1,
  },
  edit: {
    ...theme.fonts.label,
    color: theme.colors.accentAccessible,
  },
  content: {
    backgroundColor: theme.colors.grayBackground,
    borderRadius: theme.borderRadius.std,
    padding: theme.marginBase * 2,
  },
  element: {
    marginBottom: theme.marginBase * 2,
  },
  label: {
    ...theme.fonts.label,
    marginBottom: theme.marginBase / 2,
  },
  elementContent: {
    ...theme.fonts.caption,
    color: theme.colors.inkSecondary,
    whiteSpace: 'pre-line',
    display: 'flex',
    alignItems: 'center',
    gap: theme.marginBase,
  },
}));

interface Props {
  step: EditSessionStep;
  session: Session;
  children?: React.ReactNode;
  hidden?: boolean;
}

export const SessionReviewSection = ({
  step,
  session,
  children,
  hidden,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();

  const { steps } = useEditSessionSteps(session);
  const currentStep = steps.find((s) => s.name === step);
  if (!currentStep || hidden) {
    return null;
  }

  return (
    <div className={classes.container}>
      <div className={classes.topLine}>
        <h2 className={classes.title}>{currentStep.link}</h2>
        <Link className={classes.edit} to={`../${currentStep.path}`}>
          <Trans i18nKey="editSession.review.edit" />
        </Link>
      </div>
      <div className={classes.content}>{children}</div>
    </div>
  );
};

interface ElementProps {
  label?: string;
  value?: string | string[];
  children?: React.ReactNode;
  hidden?: boolean;
}

const Element = ({ label, value, children, hidden }: ElementProps) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();

  let content = children;
  if (!children && value) {
    const arrayVal = Array.isArray(value) ? value : [value];
    content = arrayVal.join(', ');

    if (!arrayVal.length) {
      content = undefined;
    }
  }

  if (!content || hidden) {
    return null;
  }

  return (
    <div className={classes.element}>
      <div className={classes.label}>{label}</div>
      <div className={classes.elementContent}>{content}</div>
    </div>
  );
};

SessionReviewSection.Element = Element;
